import { Markdown } from "@/text/Markdown";
import { classNames } from "@/utils/classNames";
import { mergeRefs } from "@/utils/reactHelpers";
import { CSSProperties, forwardRef, ReactElement, useEffect, useRef, useState } from "react";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import { useFloatingPopover } from "./floatingHelpers";
import { FloatingOverlay } from "./FloatingOverlay";
import styles from "./Tooltip.module.css";

function findTooltipTarget(el: EventTarget | null) {
  if (el === null || !(el instanceof Element || el instanceof SVGElement)) {
    return null;
  } else if ((el instanceof HTMLElement || el instanceof SVGElement) && el.dataset.tooltip) {
    return el;
  }
  return findTooltipTarget(el.parentElement);
}

export type TooltipProps = {
  open?: boolean;
  content?: ReactElement | string | number;
  label?: ReactElement | string | number;
  style?: CSSProperties;
  arrowStyle?: CSSProperties;
  arrowPosition?: "top" | "bottom";
  delay?: boolean;
  color?: "default" | "error";
  setArrowEl?: (arrowEl: HTMLDivElement | null) => void;
};

export const Tooltip = forwardRef<HTMLDivElement, TooltipProps>(
  (
    {
      open,
      content,
      label,
      style,
      arrowStyle,
      arrowPosition,
      setArrowEl,
      delay = true,
      color = "default",
    },
    ref,
  ) => {
    const internalRef = useRef<HTMLDivElement | null>(null);

    return (
      <TransitionGroup>
        {open && (
          <CSSTransition
            classNames={{
              appear: classNames("transition-opacity duration-250 opacity-0", delay && "delay-500"),
              appearActive: classNames(
                "transition-opacity duration-250 opacity-100",
                delay && "delay-500",
              ),
              enter: classNames("transition-opacity duration-250 opacity-0", delay && "delay-500"),
              enterActive: classNames(
                "transition-opacity duration-250 opacity-100",
                delay && "delay-500",
              ),
              exitActive: "opacity-0",
            }}
            nodeRef={internalRef}
            timeout={delay ? 750 : 250}
          >
            <div
              ref={mergeRefs(ref, internalRef)}
              className={classNames(
                "tooltip",
                "relative",
                "color-gray-xx-soft",
                color === "error" ? "bg-accent2" : "bg-gray-x-hard",
                "rounded-sm",
                "shadow-lg",
                "p-2",
                "pointer-events-none",
                "w-max",
                !open && "hidden",
              )}
              style={style}
            >
              <div className={"max-w-prose flex justify-between gap-4 items-center"}>
                {typeof content === "string" ? (
                  <Markdown value={content} disableLastMargin inheritLinkColor enableLinks />
                ) : (
                  <div>{content}</div>
                )}
                {label && <div className="opacity-75">{label}</div>}
              </div>
              <div
                ref={setArrowEl}
                className={classNames(
                  "tooltip-arrow",
                  "absolute",
                  "w-0",
                  "h-0",
                  "border-x-8",
                  color === "default"
                    ? styles.tooltipArrowDefault
                    : color === "error"
                      ? styles.tooltipArrowError
                      : undefined,
                  arrowPosition === "top" ? "border-b-8" : "border-t-8",
                )}
                style={{
                  ...arrowStyle,
                  top: arrowPosition === "top" ? "-8px" : undefined,
                  bottom: arrowPosition === "bottom" ? "-8px" : undefined,
                }}
              />
            </div>
          </CSSTransition>
        )}
      </TransitionGroup>
    );
  },
);

export type FocusTooltipProps = {
  children: (props: { targetRef: (element: HTMLElement | null) => void }) => ReactElement;
  placement?: "top" | "bottom";
} & Pick<TooltipProps, "color" | "content">;

export function FocusTooltip({ content, children, ...props }: FocusTooltipProps) {
  const [targetEl, setTargetEl] = useState<HTMLElement | null>(null);
  const [tooltipEl, setTooltipEl] = useState<HTMLElement | null>(null);
  const [arrowEl, setArrowEl] = useState<HTMLElement | null>(null);
  const [open, setOpen] = useState(false);
  const { floatingStyles, middlewareData, placement } = useFloatingPopover({
    open,
    popoverEl: tooltipEl,
    targetEl,
    arrowEl,
    autoUpdate: true,
    placement: props.placement,
  });

  useEffect(() => {
    if (!targetEl) {
      return;
    }

    function handleFocus() {
      if (content) {
        setOpen(true);
      }
    }

    function handleBlur() {
      setOpen(false);
    }

    targetEl.addEventListener("focus", handleFocus, true);
    targetEl.addEventListener("blur", handleBlur, true);

    if (document.activeElement === targetEl) {
      handleFocus();
    }

    return () => {
      targetEl.removeEventListener("focus", handleFocus, true);
      targetEl.removeEventListener("blur", handleFocus, true);
    };
  }, [content, targetEl]);

  return (
    <>
      {children({ targetRef: setTargetEl })}
      <FloatingOverlay layer={"tooltip"}>
        <Tooltip
          ref={setTooltipEl}
          open={open}
          content={content}
          style={floatingStyles}
          arrowPosition={placement === "bottom" ? "top" : "bottom"}
          arrowStyle={{
            left: `${middlewareData.arrow?.x ?? 0}px`,
            top: `${middlewareData.arrow?.y ?? 0}px`,
          }}
          setArrowEl={setArrowEl}
          delay={false}
          {...props}
        />
      </FloatingOverlay>
    </>
  );
}

export type AnchoredTooltipProps = {
  anchor: HTMLElement | SVGElement | null;
  placement?: "top" | "bottom";
} & Pick<TooltipProps, "color" | "content" | "delay">;

export function AnchoredTooltip({ anchor, content, placement, ...props }: AnchoredTooltipProps) {
  const [tooltipEl, setTooltipEl] = useState<HTMLElement | null>(null);
  const [arrowEl, setArrowEl] = useState<HTMLElement | null>(null);
  const open = anchor !== null && !!content;
  const {
    floatingStyles,
    middlewareData,
    placement: side,
  } = useFloatingPopover({
    open,
    popoverEl: tooltipEl,
    targetEl: anchor,
    arrowEl,
    autoUpdate: true,
    placement,
  });

  return (
    <FloatingOverlay layer={"tooltip"}>
      <Tooltip
        ref={setTooltipEl}
        open={open}
        content={content}
        style={floatingStyles}
        arrowPosition={side === "bottom" ? "top" : "bottom"}
        arrowStyle={{
          left: `${middlewareData.arrow?.x ?? 0}px`,
          top: `${middlewareData.arrow?.y ?? 0}px`,
        }}
        setArrowEl={setArrowEl}
        {...props}
      />
    </FloatingOverlay>
  );
}

export type HoverTooltipProps = {
  children: (props: { targetRef: (element: HTMLElement | null) => void }) => ReactElement;
  placement?: "top" | "bottom";
} & Pick<TooltipProps, "color" | "content">;

export function HoverTooltip({ content, children, ...props }: HoverTooltipProps) {
  const [targetEl, setTargetEl] = useState<HTMLElement | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!targetEl) {
      return;
    }

    function handleMouseEnter() {
      if (content) {
        setOpen(true);
      }
    }

    function handleMouseLeave() {
      setOpen(false);
    }

    targetEl.addEventListener("mouseenter", handleMouseEnter);
    targetEl.addEventListener("mouseleave", handleMouseLeave);
    return () => {
      targetEl.removeEventListener("mouseenter", handleMouseEnter);
      targetEl.removeEventListener("mouseleave", handleMouseEnter);
    };
  }, [content, targetEl]);

  return (
    <>
      {children({ targetRef: setTargetEl })}
      <AnchoredTooltip anchor={open ? targetEl : null} content={content} delay={false} {...props} />
    </>
  );
}

let isInitialized = false;
let touchTarget: HTMLElement | SVGElement | null = null;
let target: HTMLElement | SVGElement | null = null;

export function TooltipManager() {
  const [currentTarget, setCurrentTarget] = useState<HTMLElement | SVGElement | null>(null);
  const [tooltipEl, setTooltipEl] = useState<HTMLElement | null>(null);
  const [arrowEl, setArrowEl] = useState<HTMLElement | null>(null);
  const open = currentTarget !== null && !!currentTarget.dataset.tooltip;
  const { floatingStyles, middlewareData, placement } = useFloatingPopover({
    open,
    popoverEl: tooltipEl,
    targetEl: currentTarget,
    arrowEl,
    autoUpdate: true,
  });

  useEffect(() => {
    if (isInitialized) {
      return;
    }
    isInitialized = true;

    function mouseDownListener() {
      if (!isInitialized) {
        return;
      }
      if (target) {
        target = null;
        setCurrentTarget(null);
      }
    }

    function mouseOverListener(event: MouseEvent) {
      if (!isInitialized) {
        return;
      }
      target = findTooltipTarget(event.target);
      if (touchTarget && target === touchTarget) {
        // prevent tooltips when tapping on touch devices
        return;
      }
      if (target !== currentTarget) {
        setCurrentTarget(target);
      }
    }

    function touchEndListener(event: TouchEvent) {
      touchTarget = findTooltipTarget(event.target);
    }

    function focusListener(event: FocusEvent) {
      if (!isInitialized) {
        return;
      }
      const el = findTooltipTarget(event.target);
      if (el && el.dataset.tooltip && el.dataset.tooltipFocus) {
        setCurrentTarget(el);
      } else if (
        currentTarget &&
        currentTarget.dataset.tooltipFocus &&
        !currentTarget.contains(event.target as HTMLElement)
      ) {
        setCurrentTarget(null);
      }
    }

    function blurListener(event: FocusEvent) {
      if (!isInitialized) {
        return;
      }
      if (
        currentTarget &&
        currentTarget === event.target &&
        currentTarget.dataset.tooltipFocus &&
        !currentTarget.contains(event.relatedTarget as HTMLElement)
      ) {
        setCurrentTarget(null);
      }
    }

    document.body.addEventListener("touchend", touchEndListener);
    document.body.addEventListener("mouseover", mouseOverListener);
    document.body.addEventListener("mousedown", mouseDownListener, true);
    document.body.addEventListener("focus", focusListener, true);
    document.body.addEventListener("blur", blurListener, true);
    return () => {
      document.body.removeEventListener("touchend", touchEndListener);
      document.body.removeEventListener("mouseover", mouseOverListener);
      document.body.removeEventListener("mousedown", mouseDownListener, true);
      document.body.removeEventListener("focus", focusListener, true);
      document.body.removeEventListener("blur", blurListener, true);
      isInitialized = false;
    };
  }, [currentTarget]);

  return (
    <FloatingOverlay layer={"tooltip"}>
      <Tooltip
        ref={setTooltipEl}
        open={open}
        content={currentTarget?.dataset.tooltip}
        label={currentTarget?.dataset.tooltipLabel}
        delay={!currentTarget?.dataset.tooltipFocus}
        color={currentTarget?.dataset.tooltipColor as TooltipProps["color"]}
        style={floatingStyles}
        arrowPosition={placement === "bottom" ? "top" : "bottom"}
        arrowStyle={{
          left: `${middlewareData.arrow?.x ?? 0}px`,
          top: `${middlewareData.arrow?.y ?? 0}px`,
        }}
        setArrowEl={setArrowEl}
      />
    </FloatingOverlay>
  );
}
